// src/pages/admin/Payouts.jsx
import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/adminApi';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  TablePagination,
} from '@mui/material';
import {
  AttachMoney,
  CheckCircle,
  Cancel,
  Pending,
  Refresh,
  Visibility,
  Download,
  AccountBalance,
  Check,
  Close,
  MoreVert,
} from '@mui/icons-material';

const AdminPayouts = () => {
  // ============================================================
  // STATE
  // ============================================================
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Statistics State
  const [stats, setStats] = useState({
    pendingCount: 0,
    totalPendingAmount: 0,
    completedCount: 0,
    totalCompletedAmount: 0,
  });

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Dialog State
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Menu State
  const [anchorEl, setAnchorEl] = useState(null);

  // ============================================================
  // FETCH DATA
  // ============================================================
  const fetchPayouts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.getPayouts({
        page: page + 1,
        limit: rowsPerPage,
      });
      
      // Assuming the API returns { payouts: [], total: X, stats: {} }
      const data = response.data;
      setPayouts(data.payouts || []);
      setTotalCount(data.total || 0);
      
      if (data.stats) {
        setStats(data.stats);
      } else {
        // Fallback calculation if backend doesn't return stats
        const pendingList = data.payouts?.filter(p => p.status === 'pending') || [];
        const completedList = data.payouts?.filter(p => p.status === 'completed') || [];
        setStats({
          pendingCount: pendingList.length,
          totalPendingAmount: pendingList.reduce((sum, p) => sum + parseFloat(p.amount), 0),
          completedCount: completedList.length,
          totalCompletedAmount: completedList.reduce((sum, p) => sum + parseFloat(p.amount), 0),
        });
      }
    } catch (error) {
      setError('Failed to load payout requests. Please try again.');
      console.error('Error fetching payouts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayouts();
  }, [page, rowsPerPage]);

  // ============================================================
  // HANDLERS
  // ============================================================
  const handleMenuOpen = (event, payout) => {
    setAnchorEl(event.currentTarget);
    setSelectedPayout(payout);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewDetails = () => {
    setOpenDetailsDialog(true);
    handleMenuClose();
  };

  const handleApprovePayout = async () => {
    if (!selectedPayout) return;
    setIsProcessing(true);
    setError(null);
    setSuccess(null);

    try {
      // Call backend to update status to 'completed'
      await adminApi.updatePayoutStatus(selectedPayout.id, 'completed');
      setSuccess(`Payout of $${parseFloat(selectedPayout.amount).toFixed(2)} to ${selectedPayout.Vendor?.storeName || 'Vendor'} approved successfully!`);
      setOpenApproveDialog(false);
      fetchPayouts(); // Refresh table
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to approve payout.');
    } finally {
      setIsProcessing(false);
      setSelectedPayout(null);
    }
  };

  const handleRejectPayout = async () => {
    if (!selectedPayout) return;
    setIsProcessing(true);
    setError(null);
    setSuccess(null);

    try {
      // Call backend to update status to 'failed'
      await adminApi.updatePayoutStatus(selectedPayout.id, 'failed');
      setSuccess(`Payout request for ${selectedPayout.Vendor?.storeName || 'Vendor'} rejected.`);
      setOpenRejectDialog(false);
      fetchPayouts(); // Refresh table
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to reject payout.');
    } finally {
      setIsProcessing(false);
      setSelectedPayout(null);
    }
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ============================================================
  // UI HELPERS
  // ============================================================
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return <CheckCircle />;
      case 'pending': return <Pending />;
      case 'failed': return <Cancel />;
      default: return <Pending />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // ============================================================
  // RENDER
  // ============================================================
  if (loading && payouts.length === 0 && page === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Vendor Payouts
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review, approve, or reject vendor withdrawal requests
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchPayouts}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              disabled={payouts.length === 0}
            >
              Export
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Status Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Pending color="warning" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {stats.pendingCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Requests
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <AttachMoney color="warning" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                ${stats.totalPendingAmount?.toFixed(2) || '0.00'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Pending Amount
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircle color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {stats.completedCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completed Payouts
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <AccountBalance color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color="primary.main">
                ${stats.totalCompletedAmount?.toFixed(2) || '0.00'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Paid Out
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Payouts Table */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell>Request ID</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Available Balance</TableCell>
                <TableCell>Requested Amount</TableCell>
                <TableCell>Bank Account</TableCell>
                <TableCell>Requested At</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payouts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">
                      No payout requests found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                payouts.map((payout) => (
                  <TableRow key={payout.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="600">
                        #{payout.id}
                      </Typography>
                      {payout.transactionRef && (
                        <Typography variant="caption" color="text.secondary">
                          Ref: {payout.transactionRef}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main', fontSize: 12 }}>
                          {payout.Vendor?.storeName?.[0]?.toUpperCase() || 'V'}
                        </Avatar>
                        <Typography variant="body2">
                          {payout.Vendor?.storeName || 'Unknown Vendor'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" color="success.main">
                        ${typeof payout.Vendor?.availableBalance === 'string' 
                          ? parseFloat(payout.Vendor.availableBalance).toFixed(2) 
                          : (payout.Vendor?.availableBalance || 0).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" color="primary.main">
                        ${parseFloat(payout.amount).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {payout.Vendor?.bankAccountNumber || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {payout.Vendor?.bankName || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(payout.requestedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(payout.status)}
                        label={payout.status || 'Pending'}
                        size="small"
                        color={getStatusColor(payout.status)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, payout)}>
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* ============================================================ */}
      {/* CONTEXT MENU (Approve / Reject / View) */}
      {/* ============================================================ */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => { handleViewDetails(); }}>
          <Visibility fontSize="small" sx={{ mr: 1 }} /> View Details
        </MenuItem>
        
        {selectedPayout?.status === 'pending' && (
          <>
            <MenuItem onClick={() => { setOpenApproveDialog(true); handleMenuClose(); }} sx={{ color: 'success.main' }}>
              <Check fontSize="small" sx={{ mr: 1 }} /> Approve Payout
            </MenuItem>
            <MenuItem onClick={() => { setOpenRejectDialog(true); handleMenuClose(); }} sx={{ color: 'error.main' }}>
              <Close fontSize="small" sx={{ mr: 1 }} /> Reject Payout
            </MenuItem>
          </>
        )}
      </Menu>

      {/* ============================================================ */}
      {/* VIEW DETAILS DIALOG */}
      {/* ============================================================ */}
      <Dialog
        open={openDetailsDialog}
        onClose={() => setOpenDetailsDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AttachMoney color="primary" />
            <Typography variant="h6">Payout Request Details</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPayout && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">Vendor</Typography>
                <Typography variant="body1" fontWeight="600">
                  {selectedPayout.Vendor?.storeName || 'Unknown'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Current Available Balance</Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  ${typeof selectedPayout.Vendor?.availableBalance === 'string' 
                    ? parseFloat(selectedPayout.Vendor.availableBalance).toFixed(2) 
                    : (selectedPayout.Vendor?.availableBalance || 0).toFixed(2)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Amount Requested</Typography>
                <Typography variant="h5" fontWeight="bold" color="primary.main">
                  ${parseFloat(selectedPayout.amount).toFixed(2)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Bank Details</Typography>
                <Typography variant="body2">
                  <strong>Account:</strong> {selectedPayout.Vendor?.bankAccountNumber || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Bank:</strong> {selectedPayout.Vendor?.bankName || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Chip
                  icon={getStatusIcon(selectedPayout.status)}
                  label={selectedPayout.status || 'Pending'}
                  color={getStatusColor(selectedPayout.status)}
                />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Request Date</Typography>
                <Typography variant="body2">{formatDate(selectedPayout.requestedAt)}</Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================ */}
      {/* APPROVE DIALOG */}
      {/* ============================================================ */}
      <Dialog open={openApproveDialog} onClose={() => setOpenApproveDialog(false)} maxWidth="xs">
        <DialogTitle sx={{ color: 'success.main' }}>
          <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} /> Approve Payout
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to approve the payout of 
            <strong> ${parseFloat(selectedPayout?.amount || 0).toFixed(2)}</strong> to 
            <strong> {selectedPayout?.Vendor?.storeName || 'Vendor'}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
            This action will mark the payout as completed in the system.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenApproveDialog(false)} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApprovePayout}
            disabled={isProcessing}
          >
            {isProcessing ? <CircularProgress size={20} /> : 'Approve Payout'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================ */}
      {/* REJECT DIALOG */}
      {/* ============================================================ */}
      <Dialog open={openRejectDialog} onClose={() => setOpenRejectDialog(false)} maxWidth="xs">
        <DialogTitle sx={{ color: 'error.main' }}>
          <Cancel sx={{ mr: 1, verticalAlign: 'middle' }} /> Reject Payout
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to reject the payout request from 
            <strong> {selectedPayout?.Vendor?.storeName || 'Vendor'}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
            The request will be marked as failed and the vendor will be notified.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRejectDialog(false)} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectPayout}
            disabled={isProcessing}
          >
            {isProcessing ? <CircularProgress size={20} /> : 'Reject Payout'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPayouts;